import React, { ComponentPropsWithoutRef, ReactNode } from 'react';
import { JRCButton } from '../JRCButton/JRCButton';
import { JRCH2 } from '../JRCTypo/JRCTypo';
import { JRCIconButton } from '../JRCIconButton/JRCIconButton';
import { JRCGrid } from '../JRCGrid/JRCGrid';
import { JRCColumnCenter } from '../JRCColumnCenter/JRCColumnCenter';
import { JRCColumnLeft } from '../JRCColumnLeft/JRCColumnLeft';
import {
    JRCModalBox,
    JRCModalContainer,
    JRCModalContent,
    JRCModalFooter,
    JRCModalClose,
    ImgContainer,
    ContentContainer,
} from './JRCModalImg.styles';
import { DataCy } from '../../types/dataAttributes';

/**
 * Interface to provide Modal Buttons
 * @member color button color "primary" | "red" | "green" | "grey"
 * @member isClosed optional boolean (default false). If true, when click on the button, the modal is closes
 * @member clickHandler optional, callback when the button is click
 * @member children default ReactNode children in the Modal content
 * @member variant optional style button "contained" | "outlined"
 */
export type JRCModalImgButtonType = ComponentPropsWithoutRef<'button'> &
    DataCy & {
        children: ReactNode;
        color?: 'primary' | 'valid' | 'danger';
        variant?: 'contained' | 'outlined';
        isClosed?: boolean;
        clickHandler?: (event?: React.MouseEvent) => void;
    };

/**
 * Interface for JRCModal props
 * @member open boolean that open or close the Modal
 * @member closeHandler callback called when the Modal is closed
 * @member buttons array of JRCModalButton possibly added to the Modal
 * @member title optional string (header content) if hasHeader= true
 * @member showIconClose boolean to show/hide the close (x) button of the Modal
 * @member children default ReactNode children in the Modal content
 * @member img src & alt attributes
 * @member size modal max height
 * @member className use for overriding the styled-components styles
 */
export interface JRCModalImgProps {
    open: boolean;
    closeHandler: () => void;
    buttons?: JRCModalImgButtonType[];
    children?: ReactNode;
    title?: string;
    showIconClose?: boolean;
    img: {
        src: string;
        alt?: string;
    };
    className?: string;
}

export const JRCModalImg = ({
    open,
    closeHandler,
    buttons,
    children,
    title,
    showIconClose,
    img,
    className,
}: JRCModalImgProps) => {
    const closeModal = () => {
        closeHandler();
    };

    const escapeHandler = (e: React.KeyboardEvent) => {
        if (e.key == 'Escape') closeHandler();
    };
    const dummyVoid = (): undefined => {
        return undefined;
    };
    const getClickHandlerList = () => {
        const handlerList: (() => void)[] = [];
        if (buttons != undefined) {
            buttons.map((button: JRCModalImgButtonType) => {
                if (button.isClosed == undefined || !button.isClosed)
                    handlerList.push(() => {
                        closeHandler();
                        button.clickHandler != undefined ? button.clickHandler() : dummyVoid();
                    });
                else
                    handlerList.push(() => {
                        dummyVoid();
                    });
            });
        }
        return handlerList;
    };
    const clickHandlerList = getClickHandlerList();

    return open ? (
        <JRCModalBox onKeyDown={(e) => escapeHandler(e)} tabIndex={0} className={className}>
            <JRCModalContainer className="modal-container">
                <JRCGrid cols="2">
                    <JRCColumnLeft>
                        <ImgContainer>
                            <img src={img.src} alt={img.alt} aria-hidden={!img.alt && 'true'} />
                        </ImgContainer>
                    </JRCColumnLeft>
                    <JRCColumnCenter>
                        {showIconClose && (
                            <JRCModalClose>
                                <JRCIconButton icon="icon-times" onClick={closeModal} />
                            </JRCModalClose>
                        )}
                        <ContentContainer className="modal-content">
                            {title && <JRCH2>{title}</JRCH2>}
                            <JRCModalContent>{children}</JRCModalContent>
                        </ContentContainer>
                        {buttons ? (
                            <JRCModalFooter>
                                {buttons.map((button: JRCModalImgButtonType, index: number) => (
                                    <JRCButton
                                        key={index}
                                        color={button.color}
                                        dataCy={button.dataCy}
                                        variant={button.variant}
                                        onClick={button.isClosed ? closeModal : clickHandlerList[index]}>
                                        {button.children}
                                    </JRCButton>
                                ))}
                            </JRCModalFooter>
                        ) : null}
                    </JRCColumnCenter>
                </JRCGrid>
            </JRCModalContainer>
        </JRCModalBox>
    ) : null;
};
